import styled, { useTheme } from 'styled-components';
import React from 'react';
import {
  AssessmentTestResponse,
  AssessmentTestResult,
} from '../../../api/admin';
import { formatDuration } from './common';

const CohortsStatsWrapper = styled.div`
  padding: 20px 24px 20px 24px;
  background: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 32px;
  align-items: start;
  grid-auto-rows: minmax(0, auto);

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatItem = styled.div<{ doesNotHaveResults?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  ${({ doesNotHaveResults }) => doesNotHaveResults && 'opacity: 0.5;'}
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textRegular};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const BigNumberRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const BigNumber = styled.div`
  font-size: 44px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1;
`;

const BigUnit = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textRegular};
`;

const Caption = styled.div`
  font-size: 12px;
  margin-top: 0;
  line-height: 1.25;
  color: ${({ theme }) => theme.colors.textPlaceholder};
`;

const ProgressTrack = styled.div`
  height: 5px;
  margin: 5px 0;
  width: 100%;
  background: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ width: number }>`
  height: 100%;
  width: ${({ width }) => width}%;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 999px;
  transition: width 0.4s ease;
`;

const ProgressFillWarn = styled(ProgressFill)`
  background: ${({ theme }) => theme.colors.secondary};
`;

const CohortStats = ({
  testAssignments,
}: {
  testAssignments: AssessmentTestResponse['testAssignments'];
}) => {
  useTheme();

  const stats = React.useMemo(
    () => computeCohortStats(testAssignments ?? []),
    [testAssignments]
  );

  return (
    <CohortsStatsWrapper>
      <StatsRow>
        <StatItem>
          <StatLabel>Total participants</StatLabel>
          <BigNumberRow>
            <BigNumber>{stats.numTesters}</BigNumber>
          </BigNumberRow>
          <ProgressTrack>
            <ProgressFillWarn
              width={(stats.numNotStarted / stats.numTesters) * 100}
            />
          </ProgressTrack>
          <Caption>{stats.numNotStarted} waiting to start</Caption>
        </StatItem>

        <StatItem>
          <StatLabel>Completions</StatLabel>
          <BigNumberRow>
            <BigNumber>{stats.numCompletedTests}</BigNumber>
          </BigNumberRow>
          <ProgressTrack>
            <ProgressFill width={stats.completionPct} />
          </ProgressTrack>
          {stats.completionPct ? (
            <Caption>{stats.completionPct}% complete</Caption>
          ) : (
            <Caption>No tests completed</Caption>
          )}
        </StatItem>

        <StatItem doesNotHaveResults={stats.averageTimeLabel === null}>
          <StatLabel>Average time spent</StatLabel>
          <BigNumberRow>
            {stats.averageTimeLabel ? (
              <BigNumber>{stats.averageTimeLabel}</BigNumber>
            ) : (
              <BigNumber>-</BigNumber>
            )}
          </BigNumberRow>
          {stats.averageTimeLabel ? (
            <>
              <Caption>50% finish within {stats.p50Time}</Caption>
              <Caption>90% finish within {stats.p90Time}</Caption>
            </>
          ) : (
            <>
              <div style={{ height: 14 }} />
              <Caption>Waiting for results</Caption>
            </>
          )}
        </StatItem>

        <StatItem doesNotHaveResults={stats.averageScore === null}>
          <StatLabel>Average score</StatLabel>
          <BigNumberRow>
            {stats.averageScore ? (
              <>
                <BigNumber>{stats.averageScore}</BigNumber>
                <BigUnit>/10</BigUnit>
              </>
            ) : (
              <BigNumber>-</BigNumber>
            )}
          </BigNumberRow>
          {stats.averageScore ? (
            <>
              <Caption>
                Leading score: {stats.topSkill.label}{' '}
                {stats.topSkill.value.toFixed(1)}
              </Caption>
              <Caption>
                Lowest score: {stats.bottomSkill.label}{' '}
                {stats.bottomSkill.value.toFixed(1)}
              </Caption>
            </>
          ) : (
            <>
              <div style={{ height: 14 }} />
              <Caption>Waiting for results</Caption>
            </>
          )}
        </StatItem>
      </StatsRow>
    </CohortsStatsWrapper>
  );
};

export default CohortStats;

function computeCohortStats(
  testAssignments: AssessmentTestResponse['testAssignments']
) {
  if (!testAssignments || testAssignments.length === 0) {
    return {
      numTesters: 0,
      numCompletedTests: 0,
      numNotStarted: 0,
      completionPct: 0,
      averageTimeLabel: null,
      p50Time: formatDuration(0),
      p90Time: formatDuration(0),
      averageScore: null,
      topSkill: { label: '-', value: 0 },
      bottomSkill: { label: '-', value: 0 },
    };
  }

  const numTesters = testAssignments.length;
  const numCompletedTests = testAssignments.filter(
    (test) => test.finishedAt
  ).length;
  const numNotStarted = testAssignments.filter(
    (test) => !test.startedAt
  ).length;
  const completionPct =
    numTesters === 0 ? 0 : Math.round((numCompletedTests / numTesters) * 100);

  const secondsSpentArray = testAssignments
    .filter((test) => test.finishedAt && test.timeSpent !== null)
    .map((test) => test.timeSpent ?? 0)
    .filter((seconds) => seconds > 0);
  const totalSeconds = secondsSpentArray.reduce(
    (acc, seconds) => acc + seconds,
    0
  );
  const averageTimeSeconds = totalSeconds / secondsSpentArray.length;
  const averageTimeLabel =
    secondsSpentArray.length > 0 ? formatDuration(averageTimeSeconds) : null;

  const p50Seconds =
    secondsSpentArray.length > 0 ? quantile(secondsSpentArray, 0.5) : 0;
  const p90Seconds =
    secondsSpentArray.length > 0 ? quantile(secondsSpentArray, 0.9) : 0;
  const p50Time = formatDuration(p50Seconds);
  const p90Time = formatDuration(p90Seconds);

  const avgScoresArray = testAssignments
    .map((test) => test.results)
    .filter((results): results is AssessmentTestResult => results !== null)
    .map((results) => {
      return {
        avgScore:
          results.skillScores.reduce((acc, skill) => acc + skill.score, 0) /
          results.skillScores.length,
      };
    });
  const averageScore =
    avgScoresArray.length > 0
      ? (
          avgScoresArray.reduce((acc, score) => acc + score.avgScore, 0) /
          avgScoresArray.length
        ).toFixed(2)
      : null;

  const dimensions = testAssignments
    .map((test) => test.dimensions ?? [])
    .flat();
  const perSkillAverages = Array.from(
    testAssignments
      .map((test) => test.results?.skillScores ?? [])
      .flat()
      .reduce((acc, { skill, score }) => {
        const key =
          dimensions.find((dimension) => dimension.id === skill)?.label ?? '';

        const current = acc.get(key) ?? {
          label: key,
          total: 0,
          count: 0,
        };
        const next = {
          label: current.label,
          total: current.total + score,
          count: current.count + 1,
        };
        acc.set(key, next);
        return acc;
      }, new Map<string, { label: string; total: number; count: number }>())
  )
    .map((entry) => {
      const { label, total, count } = entry[1];
      return { label, value: total / count };
    })
    .sort((a, b) => a.value - b.value);

  const topSkill =
    perSkillAverages.length > 0
      ? perSkillAverages[perSkillAverages.length - 1]
      : { label: '-', value: 0 };
  const bottomSkill =
    perSkillAverages.length > 0
      ? perSkillAverages[0]
      : { label: '-', value: 0 };

  return {
    numTesters,
    numCompletedTests,
    numNotStarted,
    completionPct,
    averageTimeLabel,
    p50Time,
    p90Time,
    averageScore,
    topSkill,
    bottomSkill,
  };
}

const quantile = (arr: number[], q: number) => {
  const sorted = [...arr].sort((a, b) => a - b);
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  } else {
    return sorted[base];
  }
};
